import React, { useState, useEffect } from "react";

/**
 * SafeImage — wraps <img> with graceful onError fallback to prevent
 * broken-icon flashes when uploaded media (logo / flyer / QR / proof)
 * fails to load. Falls back to a VG-branded dark placeholder.
 *
 * Props:
 *   className        → shared sizing/layout applied to BOTH img and fallback
 *                      (h-12, w-24, rounded, etc.). DO NOT pass bg-* here —
 *                      it will pollute the fallback. Use imgClassName instead.
 *   imgClassName     → styles applied only to <img> (bg-white, object-contain,
 *                      p-1, border, etc.). Fallback stays VG-dark themed.
 *   fallbackClassName→ extra sizing applied only to the fallback placeholder.
 *   fallbackLabel    → short label shown inside the fallback ("LOGO", "QR",
 *                      "FLYER", "PROOF"). Defaults to "VG".
 */
const SafeImage = ({
  src,
  alt = "",
  className = "",
  imgClassName = "",
  fallbackLabel = "",
  fallbackClassName = "",
  fallbackSrc = null,
  ...rest
}) => {
  const [failed, setFailed] = useState(false);
  const [secondaryFailed, setSecondaryFailed] = useState(false);

  // Reset failure state whenever src changes so re-uploads / late-arriving
  // default media are given a fresh chance to render.
  useEffect(() => {
    setFailed(false);
    setSecondaryFailed(false);
  }, [src, fallbackSrc]);

  // Primary failed but a branded fallback image exists → render it.
  if ((!src || failed) && fallbackSrc && !secondaryFailed) {
    return (
      <img
        src={fallbackSrc}
        alt={alt}
        className={`${className} ${imgClassName}`}
        onError={() => setSecondaryFailed(true)}
        loading="lazy"
        data-testid="safe-image-fallback-src"
        {...rest}
      />
    );
  }

  if (!src || failed) {
    return (
      <div
        role="img"
        aria-label={alt || fallbackLabel || "Image unavailable"}
        className={`inline-flex items-center justify-center bg-[#141414] border border-[#1A1A1A] text-[#C6A66B] text-[10px] font-semibold tracking-widest uppercase select-none ${className} ${fallbackClassName}`}
        data-testid="safe-image-fallback"
      >
        {fallbackLabel || "VG"}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} ${imgClassName}`}
      onError={() => setFailed(true)}
      loading="lazy"
      {...rest}
    />
  );
};

export default SafeImage;
